import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { ResizableBox } from "react-resizable";
import {
  FunctionComponent,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { stylesheet } from "../../stylesheet";
import { SIENSE_WIDGET_CALLBACK_ARGS, WidgetModel } from "./types";
import {
  WidgetContainer,
  WidgetSelection,
} from "./InsightsWidgetSelectionDialog";
import { LoadingButton } from "@mui/lab";

export interface WidgetMultiselectResizeDialogProps {
  sisenseApp: any;
  selectedDashboard: string;
  widgets: any[];
  onBeforeViewLoadedHandler: (
    event: any,
    args: SIENSE_WIDGET_CALLBACK_ARGS["beforeviewloaded"],
    customChartStyles: any
  ) => void;
  isInsertingWidgets: boolean;
  onClose: () => void;
  onConfirm: (selections: WidgetSelection[]) => void;
}

export const WidgetMultiselectResizeDialog: FunctionComponent<
  WidgetMultiselectResizeDialogProps
> = ({
  sisenseApp,
  selectedDashboard,
  widgets,
  onBeforeViewLoadedHandler,
  isInsertingWidgets,
  onClose,
  onConfirm,
}) => {
  const [currentWidgets, setCurrentWidgets] = useState<any[]>();
  const widgetContainerRef = useRef<HTMLDivElement>(null);
  const [toggleStates, setToggleStates] = useState<Map<string, boolean>>(
    new Map(widgets.map((w) => [w, true]))
  );

  const [widgetContainers, setWidgetContainers] = useState<any>();
  const [containersLoaded, setContainersLoaded] = useState<boolean>(false);
  const [widgetsLoaded, setWidgetsLoaded] = useState<boolean>(false);

  const loadWidgetContainers = useCallback(() => {
    return sisenseApp.dashboards
      .load(selectedDashboard)
      .then((dashboard: any) => {
        const containers: WidgetContainer[] = [];
        // Need to reload widgets because widgets cannot be reassigned containers
        const loadedWidgets = dashboard.$$widgets.$$widgets.filter((w: any) =>
          widgets.includes(w.$$model.oid)
        );

        for (let i = 0; i < loadedWidgets.length; i++) {
          const widget = loadedWidgets[i].$$model;
          containers.push({
            widgetId: loadedWidgets[i].$$model.oid,
            id: `widgetz${i}`,
            title: widget.title,
          });
        }

        setWidgetContainers(containers);
        setContainersLoaded(true);
      });
  }, [sisenseApp.dashboards, selectedDashboard, widgets]);

  const loadWidgets = useCallback(() => {
    if (selectedDashboard && widgetContainers.length && !widgetsLoaded) {
      sisenseApp.dashboards.load(selectedDashboard).then((dashboard: any) => {
        const widgetsWithContainers: any[] = [];
        widgetContainers.forEach((container: WidgetContainer) => {
          const widget = dashboard.widgets.get(container.widgetId);
          if (!widget) return;

          const widgetElem = document.getElementById(container.id);
          if (widgetElem) {
            widget.container = widgetElem as HTMLDivElement;
            widget.on(
              "beforeviewloaded",
              (
                event: any,
                args: {
                  widget: WidgetModel;
                  element: HTMLElement;
                  options: any;
                }
              ) =>
                onBeforeViewLoadedHandler(
                  event,
                  args as SIENSE_WIDGET_CALLBACK_ARGS["beforeviewloaded"],
                  {
                    spacing: [3, 3, 3, 50],
                    margin: 1,
                  }
                )
            );

            widgetsWithContainers.push(widget);
          }
        });

        setCurrentWidgets(widgetsWithContainers);

        dashboard.refresh();
        setWidgetsLoaded(true);
      });
    }
  }, [
    selectedDashboard,
    widgetContainers,
    widgetsLoaded,
    sisenseApp.dashboards,
    onBeforeViewLoadedHandler,
  ]);

  useEffect(() => {
    loadWidgetContainers();
  }, [loadWidgetContainers]);

  useEffect(() => {
    if (containersLoaded && !widgetsLoaded) {
      loadWidgets();
    }
  });

  const handleToggleLegend = (key: string) => {
    const currentWidget = currentWidgets?.find((w) => w.$$model.oid === key);
    if (!currentWidget) return;

    const updatedToggleStates = new Map(toggleStates);
    const toggled = updatedToggleStates.get(key);
    updatedToggleStates.set(key, !toggled);
    setToggleStates(updatedToggleStates);

    currentWidget.on(
      "beforeviewloaded",
      (
        event: any,
        args: { widget: WidgetModel; element: HTMLElement; options: any }
      ) =>
        onBeforeViewLoadedHandler(
          event,
          args as SIENSE_WIDGET_CALLBACK_ARGS["beforeviewloaded"],
          {
            spacing: [3, 3, 3, 55],
            margin: 1,
            showLegend: updatedToggleStates.get(key),
          }
        )
    );
    currentWidget.refresh();
  };

  const renderContainers = function (): ReactNode {
    return (
      <div>
        {widgetContainers.map((container: WidgetContainer) => {
          const key = container.widgetId;
          const isChecked = toggleStates.get(key) || false;
          return (
            <div
              className="widget-container"
              key={key}
              ref={widgetContainerRef}
              css={(theme) => ({ marginBottom: theme.spacing(1) })}
            >
              <FormControlLabel
                style={{ marginLeft: "30px" }}
                control={
                  <Switch
                    checked={isChecked}
                    onChange={() => {
                      handleToggleLegend(key);
                    }}
                    inputProps={{ "aria-label": "controlled" }}
                  />
                }
                label={"Legend"}
              />
              <ResizableBox
                height={620}
                width={750}
                css={ss.resizable}
                onResizeStop={() => {
                  handleResize(container.widgetId);
                }}
                style={{ border: "2px solid gray" }}
              >
                <div
                  id={container.id}
                  style={{
                    width: "100%",
                    height: "100%",
                    position: "relative",
                    top: 0,
                    left: 0,
                  }}
                ></div>
              </ResizableBox>
            </div>
          );
        })}
      </div>
    );
  };

  const handleResize = (widgetId: string) => {
    const currentWidget = currentWidgets?.find(
      (w) => w.$$model.oid === widgetId
    );
    if (!currentWidget) return;

    currentWidget.on(
      "beforeviewloaded",
      (
        event: any,
        args: { widget: WidgetModel; element: HTMLElement; options: any }
      ) =>
        onBeforeViewLoadedHandler(
          event,
          args as SIENSE_WIDGET_CALLBACK_ARGS["beforeviewloaded"],
          {
            spacing: [3, 3, 3, 55],
            margin: 1,
            showLegend: toggleStates.get(widgetId),
          }
        )
    );
    currentWidget.refresh();
  };

  return (
    <Dialog
      open={true}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        style: {
          width: "800px",
          height: "820px",
        },
      }}
    >
      <DialogTitle>Resize and Insert Widgets</DialogTitle>

      {containersLoaded && <DialogContent>{renderContainers()}</DialogContent>}
      <DialogActions>
        <Button
          onClick={() => {
            setCurrentWidgets([]);
            onClose();
          }}
          color="primary"
        >
          Cancel
        </Button>
        <LoadingButton
          loading={isInsertingWidgets}
          variant="contained"
          color="primary"
          disabled={isInsertingWidgets}
          onClick={() => {
            const resizedSelections: WidgetSelection[] = [];
            widgetContainers.forEach((c: WidgetContainer) => {
              const resized = document.getElementById(c.id);
              if (resized) {
                const selection: WidgetSelection = {
                  widgetDiv: resized,
                  height: resized?.clientHeight,
                  width: resized?.clientWidth,
                };
                resizedSelections.push(selection);
              }
            });
            setCurrentWidgets([]);
            onConfirm(resizedSelections);
          }}
        >
          Confirm
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

const ss = stylesheet({
  resizable: {
    position: "relative",
    overflow: "hidden",
    "& .react-resizable-handle": {
      position: "absolute",
      width: 35,
      height: 35,
      bottom: 0,
      right: 0,
      background:
        "url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA2IDYiIHN0eWxlPSJiYWNrZ3JvdW5kLWNvbG9yOiNmZmZmZmYwMCIgeD0iMHB4IiB5PSIwcHgiIHdpZHRoPSI2cHgiIGhlaWdodD0iNnB4Ij48ZyBvcGFjaXR5PSIwLjMwMiI+PHBhdGggZD0iTSA2IDYgTCAwIDYgTCAwIDQuMiBMIDQgNC4yIEwgNC4yIDQuMiBMIDQuMiAwIEwgNiAwIEwgNiA2IEwgNiA2IFoiIGZpbGw9IiMwMDAwMDAiLz48L2c+PC9zdmc+')",
      "background-position": "bottom right",
      padding: "0 3px 3px 0",
      "background-repeat": "no-repeat",
      "background-origin": "content-box",
      "box-sizing": "border-box",
      cursor: "se-resize",
    },
  },
});
